import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    'nav.planner': 'Planner',
    'nav.spots': 'My Spots',
    'nav.equipment': 'Equipment',

    // Dashboard tabs
    'tab.overview': 'Overview',
    'tab.astro': 'Astro',
    'tab.tables': 'Tables',
    'tab.calculators': 'Calc',
    'tab.discover': 'Discover',
    'tab.alerts': 'Alerts',
    'tab.ai': 'AI',

    // Dashboard misc
    'dashboard.switchToPolish': 'Switch to Polski',
    'dashboard.switchToEnglish': 'Switch to English',
    'dashboard.switchToLightMode': 'Switch to light mode',
    'dashboard.switchToDarkMode': 'Switch to dark mode',
    'dashboard.score': 'Score',
    'dashboard.loadLocationError': 'Failed to load location data',
    'dashboard.hideLeftPanel': 'Hide left panel',
    'dashboard.showLeftPanel': 'Show left panel',
    'dashboard.hideRightPanel': 'Hide right panel',
    'dashboard.showRightPanel': 'Show right panel',
    'dashboard.tabThreeDee': '3D',
    'dashboard.tabAurora': 'Aurora',
    'dashboard.tabMeteors': 'Meteors',
    'dashboard.tabTrails': 'Trails',
    'dashboard.tabShadow': 'Shadow',
    'dashboard.tabEclipse': 'Eclipse',
    'dashboard.tabFov': 'FOV',
    'dashboard.tabDof': 'DOF',
    'dashboard.tabAr': 'AR',
    'dashboard.tabFind': 'Find',
    'dashboard.selectedDate': 'Selected',

    // Shot Score
    'shotScore.title': 'Shot Score',
    'shotScore.excellent': 'Excellent',
    'shotScore.good': 'Good',
    'shotScore.fair': 'Fair',
    'shotScore.poor': 'Poor',

    // Sun Times
    'sun.title': 'Sun Times',
    'sun.sunrise': 'Sunrise',
    'sun.sunset': 'Sunset',
    'sun.goldenHour': 'Golden Hour',
    'sun.blueHour': 'Blue Hour',
    'sun.solarNoon': 'Solar Noon',
    'sun.dayLength': 'Day Length',

    // Moon
    'moon.title': 'Moon Phase',
    'moon.illuminated': 'illuminated',
    'moon.newMoon': 'New Moon',
    'moon.fullMoon': 'Full Moon',
    'moon.waxingCrescent': 'Waxing Crescent',
    'moon.firstQuarter': 'First Quarter',
    'moon.waxingGibbous': 'Waxing Gibbous',
    'moon.waningGibbous': 'Waning Gibbous',
    'moon.lastQuarter': 'Last Quarter',
    'moon.waningCrescent': 'Waning Crescent',

    // Weather
    'weather.title': 'Weather Intelligence',
    'weather.temperature': 'Temperature',
    'weather.clouds': 'Clouds',
    'weather.wind': 'Wind',
    'weather.humidity': 'Humidity',
    'weather.visibility': 'Visibility',
    'weather.dramaticSunset': 'Sunset Drama Score',
    'weather.cloudLayers': 'Cloud Layers',
    'weather.high': 'High (Cirrus)',
    'weather.mid': 'Mid (Alto)',
    'weather.low': 'Low (Stratus)',
    'weather.prediction': 'Prediction',
    'weather.dramaticColors': 'Dramatic Colors!',
    'weather.goodPotential': 'Good Potential',
    'weather.likelyPlain': 'Likely Plain',
    'weather.cloudTip': 'High clouds = colorful skies | Low clouds = blocked light',
    'weather.temp': 'Temp',
    'weather.hourlyForecast': '12h Cloud Forecast',

    // Map
    'map.clickToSelect': 'Click map to select location',
    'map.sunLines': 'Sun Lines',
    'map.moonLines': 'Moon Lines',
    'map.showLines': 'Show Rise/Set Lines',

    // Sun/Moon map tooltips
    'sunmoon.goldenHourEnd': 'Golden Hour End',
    'sunmoon.goldenHourStart': 'Golden Hour Start',
    'sunmoon.blueHourEnd': 'Blue Hour End',

    // Milky Way
    'milkyWay.title': 'Milky Way Planner',
    'milkyWay.visibility': 'Visibility Score',
    'milkyWay.bestTime': 'Best Viewing Time',
    'milkyWay.moonInterference': 'Moon Interference',
    'milkyWay.bestNights': 'Best Nights',
    'milkyWay.lookSouth': 'Look South',
    'milkyWay.lookNorth': 'Look North',
    'milkyWay.season': 'Season',
    'milkyWay.moon': 'Moon',
    'milkyWay.maxAltitude': 'Max Altitude',
    'milkyWay.moonIs': 'Moon is',
    'milkyWay.considerWaiting': 'Consider waiting until',
    'milkyWay.newMoonParen': '(new moon).',
    'milkyWay.greatNight': 'Great Night!',
    'milkyWay.excellentConditions': "Excellent conditions for Milky Way photography. Don't miss it!",
    'milkyWay.noIdealNights': 'No ideal nights this month. Check next month.',

    // Eclipse Planner
    'eclipse.solarEclipses': 'Solar Eclipses',
    'eclipse.lunarEclipses': 'Lunar Eclipses',
    'eclipse.showPast': 'Show Past',
    'eclipse.hidePast': 'Hide Past',
    'eclipse.nextSolarEclipse': 'Next Solar Eclipse',
    'eclipse.nextLunarEclipse': 'Next Lunar Eclipse',
    'eclipse.maxDuration': 'Max duration:',
    'eclipse.days': 'days',
    'eclipse.viewDetailsOnTimeAndDate': 'View Details on TimeAndDate.com',
    'eclipse.solar': 'Solar',
    'eclipse.lunar': 'Lunar',
    'eclipse.eclipseCalendar': 'Eclipse Calendar (2024-2030)',
    'eclipse.total': 'Total',
    'eclipse.annular': 'Annular',
    'eclipse.partial': 'Partial',
    'eclipse.path': 'Path:',
    'eclipse.sarosCycle': 'Saros cycle:',
    'eclipse.visibility': 'Visibility:',
    'eclipse.magnitude': 'Magnitude:',
    'eclipse.totality': 'Totality:',
    'eclipse.viewMapAndTimes': 'View Map & Times',
    'eclipse.safetyWarningTitle': 'Safety Warning:',
    'eclipse.safetyWarningText': 'Never look directly at the sun during a solar eclipse without proper ISO-certified solar filters. Use eclipse glasses or solar viewing equipment.',
    'eclipse.photographyTips': 'Eclipse Photography Tips',
    'eclipse.tipFilterRequiredLabel': 'Solar filter required',
    'eclipse.tipFilterRequiredText': 'Remove only during totality',
    'eclipse.focalLength': 'Focal length:',
    'eclipse.tipFocalLengthSolarText': '400-800mm for sun disk detail',
    'eclipse.tipBracketingLabel': 'Bracketing:',
    'eclipse.tipBracketingText': 'Shoot multiple exposures for HDR composite',
    'eclipse.tipTotalityCaptureText': 'Capture corona, prominences, diamond ring',
    'eclipse.tipNoFilterLabel': 'No filter needed',
    'eclipse.tipNoFilterText': 'Moon is safe to photograph directly',
    'eclipse.tipFocalLengthLunarText': '200-400mm for moon detail',
    'eclipse.tipSettingsLabel': 'Settings:',
    'eclipse.tipSettingsText': 'Start at ISO 400, f/8, 1/125s, adjust during phases',
    'eclipse.tipTotalityIncreaseText': 'Increase ISO to 1600-6400 for dim red moon',

    // Meteor Showers
    'meteor.nextShower': 'Next Meteor Shower',
    'meteor.peak': 'Peak:',
    'meteor.days': 'days',
    'meteor.activeNow': 'ACTIVE NOW',
    'meteor.zhrLabel': 'ZHR (meteors/h)',
    'meteor.showerCalendar': 'Meteor Shower Calendar',
    'meteor.active': 'Active',
    'meteor.veryGood': 'Very Good',
    'meteor.activePeriod': 'Active Period',
    'meteor.moonInterference': 'Moon Interference',
    'meteor.illumination': 'illumination',
    'meteor.speed': 'Speed',
    'meteor.radiantAlt': 'Radiant Alt.',
    'meteor.max': 'max',
    'meteor.parent': 'Parent:',
    'meteor.radiant': 'Radiant:',
    'meteor.peakIn': 'Peak in',
    'meteor.startPlanningShoot': 'start planning your shoot!',
    'meteor.photographyTips': 'Meteor Photography Tips',
    'meteor.bestTime': 'Best time:',
    'meteor.tipBestTimeText': 'After midnight when radiant is highest',
    'meteor.settings': 'Settings:',
    'meteor.tipSettingsText': 'Wide angle, f/2.8 or wider, ISO 3200-6400, 15-25s exposures',
    'meteor.tip': 'Tip:',
    'meteor.tipFramingText': 'Frame away from radiant for longer meteor trails',
    'meteor.stack': 'Stack:',
    'meteor.tipStackText': 'Take 100+ photos and composite the best meteors',

    // Aurora Planner
    'aurora.title': 'Aurora Borealis Planner',
    'aurora.liveForecast': 'Live Forecast',
    'aurora.currentKpIndex': 'Current Kp Index',
    'aurora.extremeStorm': 'Extreme Storm',
    'aurora.severeStorm': 'Severe Storm',
    'aurora.strongStorm': 'Strong Storm',
    'aurora.moderateStorm': 'Moderate Storm',
    'aurora.minorStorm': 'Minor Storm',
    'aurora.active': 'Active',
    'aurora.unsettled': 'Unsettled',
    'aurora.quiet': 'Quiet',
    'aurora.yourVisibility': 'Your Visibility',
    'aurora.possibleAtLatitude': 'Aurora possible at your latitude!',
    'aurora.needKp': 'Need Kp',
    'aurora.forVisibilityAt': 'for visibility at',
    'aurora.kpIndexScale': 'Kp Index Scale',
    'aurora.storm': 'Storm',
    'aurora.yourLocation': 'Your Location:',
    'aurora.inAuroraZone': '🎯 You are in the aurora zone! Great location for northern lights.',
    'aurora.visibleStrongStorms': '⚡ Aurora visible during strong geomagnetic storms (Kp 5+)',
    'aurora.visibleExtremeStorms': '🌟 Aurora visible only during extreme storms (Kp 7+)',
    'aurora.tooFarSouth': '📍 You are too far south for aurora. Travel north for better chances.',
    'aurora.bestViewingTimes': 'Best Viewing Times (Next 24h)',
    'aurora.peakForecast': 'Peak Forecast (3 days)',
    'aurora.visible': 'Visible!',
    'aurora.kpForecast72h': '72-Hour Kp Forecast',
    'aurora.night': 'Night',
    'aurora.day': 'Day',
    'aurora.photographyTips': 'Aurora Photography Tips',
    'aurora.settings': 'Settings:',
    'aurora.tipSettingsText': 'ISO 1600-6400, f/2.8 or wider, 10-25 sec exposure',
    'aurora.bestTime': 'Best time:',
    'aurora.tipBestTimeText': '10 PM - 2 AM local time, during new moon',
    'aurora.direction': 'Direction:',
    'aurora.tipDirectionText': 'Look north (or south in southern hemisphere)',
    'aurora.location': 'Location:',
    'aurora.tipLocationText': 'Find dark skies away from light pollution',

    // Star Trails Simulator
    'startrails.title': 'Star Trails Simulator',
    'startrails.northernHemisphere': 'Northern Hemisphere',
    'startrails.southernHemisphere': 'Southern Hemisphere',
    'startrails.exposure': 'Exposure:',
    'startrails.cameraDirection': 'Camera Direction:',
    'startrails.focalLength': 'Focal Length:',
    'startrails.trailLength': 'Trail Length',
    'startrails.quick': 'Quick:',
    'startrails.preset15': '15° trail',
    'startrails.preset45': '45° trail',
    'startrails.preset90': '90° trail',
    'startrails.presetFullCircle': 'Full circle',
    'startrails.animating': 'Animating...',
    'startrails.animateTrails': 'Animate Trails',
    'startrails.north': 'North',
    'startrails.trailCalculations': 'Trail Calculations',
    'startrails.trailArc': 'Trail Arc',
    'startrails.totalTime': 'Total Time',
    'startrails.formulaLabel': 'Formula:',
    'startrails.fullCircleLabel': 'Full circle (360°):',
    'startrails.hours': 'hours',
    'startrails.tips': 'Star Trails Tips',
    'startrails.circumpolar': 'Circumpolar:',
    'startrails.pointAt': 'Point at',
    'startrails.polarisNorth': 'Polaris (North)',
    'startrails.southCelestialPole': 'South Celestial Pole',
    'startrails.forCircularTrails': 'for circular trails',
    'startrails.equatorial': 'Equatorial:',
    'startrails.tipEquatorialText': 'Point East/West for straight diagonal trails',
    'startrails.stacking': 'Stacking:',
    'startrails.tipStackingText': 'Take multiple 30s exposures and stack in post',
    'startrails.gapFree': 'Gap-free:',
    'startrails.tipGapFreeText': 'Use 1-2s interval between shots, or bulb mode',
    'startrails.yourLatitude': 'Your latitude:',
    'startrails.circumpolarStarsWithin': '— circumpolar stars within',
    'startrails.ofPole': 'of pole',
    'startrails.polaris': 'Polaris',
    'startrails.southPole': 'South Pole',
    'startrails.canvasTrail': 'Trail:',

    // Calculators
    'calc.title': 'Photo Calculators',
    'calc.exposure': 'Exposure',
    'calc.dof': 'DOF',
    'calc.starTrails': 'Trails',
    'calc.iso': 'ISO',
    'calc.aperture': 'Aperture',
    'calc.shutterSpeed': 'Shutter Speed',
    'calc.focalLength': 'Focal Length',
    'calc.distance': 'Distance',
    'calc.apertureF': 'Aperture (f/)',
    'calc.lightCondition': 'Light Condition (EV)',
    'calc.ev': 'EV',
    'calc.evBrightSunOnSnow': 'Bright sun on snow',
    'calc.evSunny': 'Sunny (Sunny 16)',
    'calc.evSlightOvercast': 'Slight overcast',
    'calc.evOvercast': 'Overcast',
    'calc.evHeavyOvercast': 'Heavy overcast',
    'calc.evOpenShade': 'Open shade',
    'calc.evBrightIndoor': 'Bright indoor',
    'calc.evIndoor': 'Indoor',
    'calc.evDimIndoor': 'Dim indoor',
    'calc.evCandlelight': 'Candlelight',
    'calc.evNightStreet': 'Night street',
    'calc.evFullMoon': 'Full moon',
    'calc.evQuarterMoon': 'Quarter moon',
    'calc.evStarlight': 'Starlight',
    'calc.evMilkyWay': 'Milky Way',
    'calc.focalLengthMm': 'Focal Length (mm)',
    'calc.distanceM': 'Distance (m)',
    'calc.sensorMm': 'Sensor (mm)',
    'calc.nearFocus': 'Near Focus',
    'calc.farFocus': 'Far Focus',
    'calc.totalDof': 'Total Depth of Field',
    'calc.hyperfocalDistance': 'Hyperfocal Distance',
    'calc.focusHereFrom': 'Focus here for maximum sharpness from',
    'calc.toInfinity': 'to ∞',
    'calc.cropFactor': 'Crop Factor',
    'calc.pointStars': 'Point Stars (no trails)',
    'calc.rule500': '500 Rule',
    'calc.starTrailsDuration': 'Star Trails Duration',
    'calc.short': 'Short',
    'calc.medium': 'Medium',
    'calc.long': 'Long',
    'calc.forOneHourStackedTrails': 'For 1-hour stacked trails',

    // DOF Calculator
    'dof.title': 'Depth of Field Calculator',
    'dof.dofLabel': 'DOF',
    'dof.sensorSize': 'Sensor Size',
    'dof.sensorFullFrame': 'Full Frame',
    'dof.sensorApsCCanon': 'APS-C Canon',
    'dof.sensorApsC': 'APS-C Nikon/Sony',
    'dof.sensorMicro43': 'Micro 4/3',
    'dof.sensorMediumFormat': 'Medium Format',
    'dof.sensor1Inch': '1 inch',
    'dof.focal': 'Focal',
    'dof.focus': 'Focus',
    'dof.focusAtHyperfocal': 'Focus at Hyperfocal',
    'dof.results': 'DOF Results',
    'dof.nearLimit': 'Near Limit',
    'dof.farLimit': 'Far Limit',
    'dof.totalDof': 'Total DOF',
    'dof.hyperfocalDistance': 'Hyperfocal Distance',
    'dof.focusHereMaxSharpness': 'Focus here for max sharpness to ∞',
    'dof.dofDistribution': 'DOF Distribution',
    'dof.front': 'front',
    'dof.back': 'back',
    'dof.oneThirdRule': '~1/3 front, ~2/3 back rule',
    'dof.tipPortraitsLabel': 'Portraits:',
    'dof.tipPortraitsDesc': 'Wide aperture (f/1.8-2.8) = shallow DOF, blurry background',
    'dof.tipLandscapesLabel': 'Landscapes:',
    'dof.tipLandscapesDesc': 'Small aperture (f/8-16) = deep DOF, everything sharp',
    'dof.tipHyperfocalLabel': 'Hyperfocal:',
    'dof.tipHyperfocalDesc': 'Focus here for sharpness from half-HF to infinity',
    'dof.tipRuleLabel': '1/3 Rule:',
    'dof.tipRuleDesc': 'DOF extends ~1/3 in front, ~2/3 behind focus point',
    'dof.near': 'Near',
    'dof.far': 'Far',
    'dof.hf': 'HF',

    // FOV Calculator
    'fov.title': 'Field of View Calculator',
    'fov.lensUltraWide': 'Ultra Wide',
    'fov.lensWide': 'Wide',
    'fov.lensNormal': 'Normal',
    'fov.lensPortrait': 'Portrait',
    'fov.lensTelephoto': 'Telephoto',
    'fov.lensSuperTele': 'Super Tele',
    'fov.sensorSize': 'Sensor Size',
    'fov.sensorFullFrame': 'Full Frame (35mm)',
    'fov.sensorApsCCanon': 'APS-C (Canon)',
    'fov.sensorApsC': 'APS-C (Nikon/Sony)',
    'fov.sensorMicro43': 'Micro 4/3',
    'fov.sensorMediumFormat': 'Medium Format',
    'fov.sensor1Inch': '1 inch',
    'fov.focalLength': 'Focal Length',
    'fov.equiv': 'equiv.',
    'fov.distance': 'Distance',
    'fov.cameraDirection': 'Camera Direction',
    'fov.fovShownOnMap': 'FOV Shown on Map',
    'fov.showFovOnMap': 'Show FOV on Map',
    'fov.details': 'FOV Details',
    'fov.horizontal': 'Horizontal',
    'fov.vertical': 'Vertical',
    'fov.diagonal': 'Diagonal',
    'fov.coverage': 'Coverage',
    'fov.hyperfocalF8': 'Hyperfocal (f/8)',
    'fov.useLandscapeLabel': 'Landscape:',
    'fov.useLandscapeDesc': '14-35mm (Wide FOV: 63-114°)',
    'fov.usePortraitLabel': 'Portrait:',
    'fov.usePortraitDesc': '85-135mm (Narrow FOV: 15-24°)',
    'fov.useWildlifeLabel': 'Wildlife:',
    'fov.useWildlifeDesc': '400-600mm (Very narrow: 3-5°)',
    'fov.useAstroLabel': 'Astro:',
    'fov.useAstroDesc': '14-24mm (Wide for Milky Way)',

    // Shadow Calculator
    'shadow.presetPerson': 'Person (1.75m)',
    'shadow.presetTree': 'Tree (8m)',
    'shadow.presetBuilding': 'Building (15m)',
    'shadow.presetTower': 'Tower (50m)',
    'shadow.presetCustom': 'Custom',
    'shadow.title': 'Shadow Calculator',
    'shadow.shadowLabel': 'Shadow',
    'shadow.noShadow': 'No Shadow',
    'shadow.noShadowSunBelowHorizon': 'No shadow (sun below horizon)',
    'shadow.time': 'Time',
    'shadow.sunAlt': 'Sun Alt',
    'shadow.sunAz': 'Sun Az',
    'shadow.objectHeight': 'Object Height',
    'shadow.pause': 'Pause',
    'shadow.animateDay': 'Animate Day',
    'shadow.shadowLength': 'Shadow Length',
    'shadow.timesObjectHeight': '× object height',
    'shadow.sunBelowHorizon': 'Sun below horizon',
    'shadow.shadowDirection': 'Shadow Direction',
    'shadow.oppositeToSun': 'Opposite to sun',
    'shadow.formulaLabel': 'Formula:',
    'shadow.formulaText': 'Shadow Length = Object Height / tan(Sun Altitude)',
    'shadow.currentSunLabel': 'Current Sun:',
    'shadow.alt': 'Alt',
    'shadow.az': 'Az',

    // Ephemeris
    'ephemeris.title': 'Ephemeris Tables',
    'ephemeris.today': 'Today',
    'ephemeris.week': '7 Days',
    'ephemeris.moonCal': 'Moon Cal',
    'ephemeris.twilight': 'Twilight Times',
    'ephemeris.astronomical': 'Astronomical',
    'ephemeris.nautical': 'Nautical',
    'ephemeris.civil': 'Civil',
    'ephemeris.daylight': 'Daylight',
    'ephemeris.moonToday': 'Moon Today',
    'ephemeris.date': 'Date',
    'ephemeris.length': 'Length',
    'ephemeris.moon': 'Moon',
    'ephemeris.newMoons': 'New Moons',
    'ephemeris.fullMoons': 'Full Moons',
    'ephemeris.noneThisMonth': 'None this month',
    'ephemeris.weekDaysShort': 'S,M,T,W,T,F,S',

    // Timeline 2D
    'timeline.title': '24h Timeline',
    'timeline.horizon': 'Horizon',
    'timeline.selectedTime': 'Selected Time',
    'timeline.sunAltitude': 'Sun Altitude',
    'timeline.conditions': 'Conditions',
    'timeline.night': 'Night',

    // Find Sun/Moon
    'findsunmoon.title': 'Find Sun/Moon Position',
    'findsunmoon.sun': 'Sun',
    'findsunmoon.moon': 'Moon',
    'findsunmoon.targetAzimuth': 'Target Azimuth',
    'findsunmoon.targetElevation': 'Target Elevation',
    'findsunmoon.horizon': 'Horizon',
    'findsunmoon.low': 'Low',
    'findsunmoon.mid': 'Mid',
    'findsunmoon.high': 'High',
    'findsunmoon.advancedFilters': 'Advanced Filters',
    'findsunmoon.azTolerance': 'Az Tolerance',
    'findsunmoon.elTolerance': 'El Tolerance',
    'findsunmoon.daysToSearch': 'Days to Search',
    'findsunmoon.searching': 'Searching...',
    'findsunmoon.findDates': 'Find Dates',
    'findsunmoon.found': 'Found',
    'findsunmoon.results': 'Results',
    'findsunmoon.sortDate': 'Date',
    'findsunmoon.sortElevation': 'Elevation',
    'findsunmoon.sortPhase': 'Phase',
    'findsunmoon.day': 'Day',
    'findsunmoon.astroTwilight': 'Astro Twilight',
    'findsunmoon.nauticalTwilight': 'Nautical Twilight',
    'findsunmoon.az': 'Az',
    'findsunmoon.el': 'El',
    'findsunmoon.useCaseLabel': 'Use case:',
    'findsunmoon.useCaseText': 'Find when sun sets behind a specific landmark',
    'findsunmoon.azimuthLabel': 'Azimuth:',
    'findsunmoon.azimuthText': '0°=N, 90°=E, 180°=S, 270°=W',
    'findsunmoon.clickResultLabel': 'Click result',
    'findsunmoon.clickResultText': 'to load that date in the planner',

    // Map Layers
    'maplayers.layersButton': 'Layers',
    'maplayers.mapLayers': 'Map Layers',
    'maplayers.goldenHourZone': 'Golden Hour Zone',
    'maplayers.blueHourZone': 'Blue Hour Zone',
    'maplayers.shadowDirection': 'Shadow Direction',
    'maplayers.fovCone': 'FOV Cone',
    'maplayers.twilightZones': 'Twilight Zones',
    'maplayers.fovSettings': 'FOV Settings',
    'maplayers.focalLength': 'Focal Length',
    'maplayers.direction': 'Direction',
    'maplayers.distance': 'Distance',
    'maplayers.allOn': 'All On',
    'maplayers.allOff': 'All Off',

    // Spots
    'spots.title': 'My Spots',
    'spots.addSpot': 'Add Spot',
    'spots.noSpots': 'No spots saved yet',
    'spots.useMyLocation': 'Use My Location',
    'spots.saveCurrentLocation': 'Save this location to My Spots',
    'spots.saveLocationPrompt': 'Name this spot',
    'spots.saveLocationSuccess': 'Spot saved!',
    'spots.saveLocationError': 'Failed to save spot. Please log in first.',
    'spots.nameRequired': 'Please enter a spot name',
    'spots.createdSuccess': 'Spot created successfully!',
    'spots.createError': 'Failed to create spot',
    'spots.deletedSuccess': 'Spot deleted',
    'spots.deleteError': 'Failed to delete spot',
    'spots.locationUpdated': 'Location updated',
    'spots.locationError': 'Could not get your location',
    'spots.createSpot': 'Add New Spot',
    'spots.spotName': 'Name',
    'spots.namePlaceholder': 'e.g., Mountain Peak Vista',
    'spots.description': 'Description',
    'spots.descriptionPlaceholder': 'Notes about this location...',
    'spots.location': 'Location',
    'spots.clickMapHint': 'Click on the map to set location',
    'spots.latitude': 'Latitude',
    'spots.longitude': 'Longitude',
    'spots.saveSpot': 'Save Spot',
    'spots.noSpotsHint': 'Click "Add Spot" to save your first location',

    // Discover
    'discover.title': 'Spot Discovery',
    'discover.search': 'Search spots...',
    'discover.all': 'All',
    'discover.mountains': 'Mountains',
    'discover.urban': 'Urban',
    'discover.nature': 'Nature',
    'discover.astro': 'Astro',
    'discover.nearest': 'Nearest',
    'discover.rating': 'Rating',
    'discover.score': 'Shot Score',
    'discover.minScore': 'Min Score',
    'discover.sort': 'Sort',
    'discover.spotsFound': 'spots found',
    'discover.by': 'by',
    'discover.away': 'away',
    'discover.reviews': 'reviews',
    'discover.noSpotsFound': 'No spots found',
    'discover.tryAdjustingFilters': 'Try adjusting your filters',
    'discover.shareYourSpots': 'Share Your Spots',
    'discover.helpCommunity': 'Help the community discover great locations',
    'discover.contribute': 'Contribute',
    'discover.selected': 'Selected',
    'discover.spotSaved': 'Spot saved to your collection!',
    'discover.spotSaveFailed': 'Failed to save spot. Please login first.',

    // Alerts
    'alerts.title': 'Smart Alerts',
    'alerts.enableNotifications': 'Enable Notifications',
    'alerts.addAlert': 'Add Alert',
    'alerts.goldenHour': 'Golden Hour',
    'alerts.blueHour': 'Blue Hour',
    'alerts.shotScore': 'Shot Score Threshold',
    'alerts.newMoon': 'New Moon (Astro)',
    'alerts.startsIn': 'starts in',
    'alerts.minutesLabel': 'minutes',
    'alerts.shotScoreIs': 'Shot Score is',
    'alerts.greatConditions': 'Great conditions!',
    'alerts.newMoonTonight': 'New Moon tonight',
    'alerts.perfectForAstro': 'Perfect for astrophotography!',
    'alerts.notificationsNotSupported': 'Notifications not supported in this browser',
    'alerts.notificationsEnabledMsg': 'Notifications enabled!',
    'alerts.notificationPermissionDenied': 'Notification permission denied',
    'alerts.alertCreated': 'Alert created!',
    'alerts.alertDeleted': 'Alert deleted',
    'alerts.noAlertsConfigured': 'No alerts configured',
    'alerts.noAlertsHint': 'Add alerts to get notified about perfect conditions',
    'alerts.whenScoreAbove': 'When score ≥',
    'alerts.minBefore': 'min before',
    'alerts.alertType': 'Alert Type',
    'alerts.scoreThreshold': 'Score Threshold',
    'alerts.notifyBefore': 'Notify Before',

    // AI
    'ai.title': 'AI Shot Prediction',
    'ai.getRecommendation': 'Get Recommendation',
    'ai.analyzing': 'Analyzing...',
    'ai.todayRecommendation': "Today's Recommendation",
    'ai.landscape': 'Landscape',
    'ai.goldenHour': 'Golden Hour',
    'ai.blueHour': 'Blue Hour',
    'ai.astrophotography': 'Astrophotography',
    'ai.urban': 'Urban/Architecture',

    // AR Camera View
    'ar.title': 'AR Camera View',
    'ar.live': 'LIVE',
    'ar.cameraErrorDefault': 'Could not access camera',
    'ar.httpsHint': "Make sure camera permissions are granted and you're using HTTPS",
    'ar.pointCameraHint': 'Point your camera at the sky to see sun and moon positions overlaid',
    'ar.startCamera': 'Start Camera',
    'ar.timeOffset': 'Time Offset',
    'ar.manualHeading': 'Manual Heading',
    'ar.stopCamera': 'Stop Camera',
    'ar.sun': 'Sun',
    'ar.moon': 'Moon',
    'ar.azimuth': 'Azimuth',
    'ar.altitude': 'Altitude',
    'ar.heading': 'Heading',
    'ar.time': 'Time',
    'ar.tipMobileTitle': 'Best on mobile:',
    'ar.tipMobileText': "Use phone's compass for accurate heading",
    'ar.tipCalibrateTitle': 'Calibrate:',
    'ar.tipCalibrateText': 'Move phone in figure-8 pattern to calibrate compass',
    'ar.tipTimeScrubTitle': 'Time scrub:',
    'ar.tipTimeScrubText': 'Adjust time offset to see future positions',
    'ar.tipUseCaseTitle': 'Use case:',
    'ar.tipUseCaseText': 'Find exact spot where sun will set behind landmark',

    // View 3D Planner
    'view3d.seeRealPhotos': 'See real photos from this location:',
    'view3d.realStreetView': 'Real Street View',
    'view3d.sunPositionSimulator': 'Sun Position Simulator',
    'view3d.daylight': 'Daylight',
    'view3d.twilight': 'Twilight',
    'view3d.night': 'Night',
    'view3d.direction': 'Direction',
    'view3d.look': 'Look',
    'view3d.focal': 'Focal',
    'view3d.time': 'Time',
    'view3d.pause': 'Pause',
    'view3d.animateDay': 'Animate Day',
    'view3d.faceSun': 'Face Sun',
    'view3d.goldenAM': 'Golden AM',
    'view3d.noon': 'Noon',
    'view3d.goldenPM': 'Golden PM',
    'view3d.sunAzimuth': 'Sun Azimuth',
    'view3d.sunAltitude': 'Sun Altitude',
    'view3d.cameraFov': 'Camera FOV',

    // Equipment
    'equipment.title': 'My Equipment',
    'equipment.addEquipment': 'Add Equipment',
    'equipment.camera': 'Camera Body',
    'equipment.lens': 'Lens',
    'equipment.tripod': 'Tripod/Support',
    'equipment.filter': 'Filter',
    'equipment.drone': 'Drone',
    'equipment.accessory': 'Accessory',
    'equipment.other': 'Other',
    'equipment.filterByCategory': 'Filter by category',
    'equipment.allCategories': 'All Categories',
    'equipment.name': 'Name',
    'equipment.namePlaceholder': 'e.g., Canon EOS R5',
    'equipment.category': 'Category',
    'equipment.notes': 'Notes',
    'equipment.notesPlaceholder': 'Serial number, settings, etc.',
    'equipment.noEquipmentTitle': 'No Equipment Yet',
    'equipment.noEquipmentSubtitle': 'Start building your gear checklist',
    'equipment.addFirstItem': 'Add Your First Item',
    'equipment.nameRequired': 'Please enter equipment name',
    'equipment.addedSuccess': 'Equipment added successfully!',
    'equipment.addError': 'Failed to add equipment',
    'equipment.deletedSuccess': 'Equipment deleted',
    'equipment.deleteError': 'Failed to delete equipment',

    // Auth
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.logout': 'Logout',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.name': 'Name',
    'auth.signIn': 'Sign In',
    'auth.createAccount': 'Create Account',
    'auth.welcomeBack': 'Welcome back',
    'auth.createYourAccount': 'Create your account',
    'auth.welcomeBackToast': 'Welcome back!',
    'auth.loginFailed': 'Login failed',
    'auth.backToHome': 'Back to home',
    'auth.signInSubtitle': 'Sign in to continue planning perfect light',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.noAccount': "Don't have an account?",
    'auth.createOne': 'Create one',
    'auth.passwordMinLength': 'Password must be at least 6 characters',
    'auth.accountCreated': 'Account created successfully!',
    'auth.registrationFailed': 'Registration failed',
    'auth.registerSubtitle': 'Start planning your perfect shots today',
    'auth.namePlaceholder': 'Your name',
    'auth.passwordMinPlaceholder': 'Min. 6 characters',
    'auth.haveAccount': 'Already have an account?',

    // Landing
    'landing.hero': 'Plan Perfect Light.',
    'landing.subtitle': 'Stop guessing. LuminaSpot analyzes thousands of variables to tell you exactly where and when to capture your best shot.',
    'landing.getStarted': 'Start Planning Free',
    'landing.features': 'Everything You Need to Plan',
    'landing.featureSunTitle': 'Sun & Moon Tracking',
    'landing.featureSunDesc': 'Precise calculations for sunrise, sunset, golden hour, and blue hour. Know exactly when the light is perfect.',
    'landing.featureScoreDesc': 'Our algorithm analyzes weather, visibility, and astronomical conditions to rate your shooting potential from 0-100%.',
    'landing.featureScoutingTitle': 'Smart Scouting',
    'landing.featureScoutingDesc': 'Save your favorite locations with GPS coordinates. Access them anywhere, plan ahead.',
    'landing.featureWeatherDesc': 'Multi-layer cloud analysis, visibility forecasts, and atmospheric conditions at a glance.',
    'landing.getStartedNav': 'Get Started',
    'landing.badge': 'Next-Gen Photography Planning',
    'landing.heroLine1': 'Plan Perfect',
    'landing.heroLine2': 'Light.',
    'landing.haveAccount': 'I have an account',
    'landing.statWeather': 'Weather Forecast',
    'landing.statAccuracy': 'Shot Score Accuracy',
    'landing.statLocations': 'Saved Locations',
    'landing.featuresSubtitle': 'Professional tools inspired by PhotoPills, redesigned for the modern photographer',
    'landing.ctaTitlePrefix': 'Ready to Capture',
    'landing.ctaTitleHighlight': 'Perfect Light',
    'landing.ctaSubtitle': 'Join photographers who plan smarter, not harder. Your next masterpiece starts with perfect planning.',
    'landing.createFreeAccount': 'Create Free Account',
    'landing.footerTagline': '© 2025 LuminaSpot. Plan Perfect Light.',

    // Settings
    'settings.title': 'Settings',
    'settings.theme': 'Theme',
    'settings.light': 'Light',
    'settings.dark': 'Dark',
    'settings.auto': 'Auto',
    'settings.language': 'Language',

    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.morning': 'Morning',
    'common.evening': 'Evening',
  },

  pl: {
    // Navigation
    'nav.planner': 'Planer',
    'nav.spots': 'Moje Miejsca',
    'nav.equipment': 'Sprzęt',

    // Dashboard tabs
    'tab.overview': 'Przegląd',
    'tab.astro': 'Astro',
    'tab.tables': 'Tabele',
    'tab.calculators': 'Kalk.',
    'tab.discover': 'Odkryj',
    'tab.alerts': 'Alerty',
    'tab.ai': 'AI',

    // Dashboard misc
    'dashboard.switchToPolish': 'Przełącz na polski',
    'dashboard.switchToEnglish': 'Przełącz na angielski',
    'dashboard.switchToLightMode': 'Przełącz na jasny motyw',
    'dashboard.switchToDarkMode': 'Przełącz na ciemny motyw',
    'dashboard.score': 'Wynik',
    'dashboard.loadLocationError': 'Nie udało się załadować danych lokalizacji',
    'dashboard.hideLeftPanel': 'Ukryj lewy panel',
    'dashboard.showLeftPanel': 'Pokaż lewy panel',
    'dashboard.hideRightPanel': 'Ukryj prawy panel',
    'dashboard.showRightPanel': 'Pokaż prawy panel',
    'dashboard.tabThreeDee': '3D',
    'dashboard.tabAurora': 'Zorza',
    'dashboard.tabMeteors': 'Meteory',
    'dashboard.tabTrails': 'Ślady',
    'dashboard.tabShadow': 'Cień',
    'dashboard.tabEclipse': 'Zaćmienie',
    'dashboard.tabFov': 'FOV',
    'dashboard.tabDof': 'DOF',
    'dashboard.tabAr': 'AR',
    'dashboard.tabFind': 'Znajdź',
    'dashboard.selectedDate': 'Wybrano',

    // Shot Score
    'shotScore.title': 'Ocena Ujęcia',
    'shotScore.excellent': 'Doskonała',
    'shotScore.good': 'Dobra',
    'shotScore.fair': 'Średnia',
    'shotScore.poor': 'Słaba',

    // Sun Times
    'sun.title': 'Czasy Słońca',
    'sun.sunrise': 'Wschód',
    'sun.sunset': 'Zachód',
    'sun.goldenHour': 'Złota Godzina',
    'sun.blueHour': 'Błękitna Godzina',
    'sun.solarNoon': 'Południe Słoneczne',
    'sun.dayLength': 'Długość Dnia',

    // Moon
    'moon.title': 'Faza Księżyca',
    'moon.illuminated': 'oświetlony',
    'moon.newMoon': 'Nów',
    'moon.fullMoon': 'Pełnia',
    'moon.waxingCrescent': 'Młody Księżyc',
    'moon.firstQuarter': 'Pierwsza Kwadra',
    'moon.waxingGibbous': 'Rosnący Garbaty',
    'moon.waningGibbous': 'Malejący Garbaty',
    'moon.lastQuarter': 'Trzecia Kwadra',
    'moon.waningCrescent': 'Stary Księżyc',

    // Weather
    'weather.title': 'Analiza Pogody',
    'weather.temperature': 'Temperatura',
    'weather.clouds': 'Chmury',
    'weather.wind': 'Wiatr',
    'weather.humidity': 'Wilgotność',
    'weather.visibility': 'Widoczność',
    'weather.dramaticSunset': 'Potencjał Zachodu',
    'weather.cloudLayers': 'Warstwy Chmur',
    'weather.high': 'Wysokie (Cirrus)',
    'weather.mid': 'Średnie (Alto)',
    'weather.low': 'Niskie (Stratus)',
    'weather.prediction': 'Prognoza',
    'weather.dramaticColors': 'Dramatyczne kolory!',
    'weather.goodPotential': 'Dobry potencjał',
    'weather.likelyPlain': 'Raczej bez kolorów',
    'weather.cloudTip': 'Wysokie chmury = kolorowe niebo, niskie chmury = zasłonięte światło',
    'weather.temp': 'Temp.',
    'weather.hourlyForecast': 'Prognoza zachmurzenia (12h)',

    // Map
    'map.clickToSelect': 'Kliknij mapę aby wybrać lokalizację',
    'map.sunLines': 'Linie Słońca',
    'map.moonLines': 'Linie Księżyca',
    'map.showLines': 'Pokaż linie wschodu/zachodu',

    // Sun/Moon map tooltips
    'sunmoon.goldenHourEnd': 'Koniec złotej godziny',
    'sunmoon.goldenHourStart': 'Początek złotej godziny',
    'sunmoon.blueHourEnd': 'Koniec błękitnej godziny',

    // Milky Way
    'milkyWay.title': 'Planer Drogi Mlecznej',
    'milkyWay.visibility': 'Ocena Widoczności',
    'milkyWay.bestTime': 'Najlepszy Czas',
    'milkyWay.moonInterference': 'Interferencja Księżyca',
    'milkyWay.bestNights': 'Najlepsze Noce',
    'milkyWay.lookSouth': 'Patrz na Południe',
    'milkyWay.lookNorth': 'Patrz na Północ',
    'milkyWay.season': 'Sezon',
    'milkyWay.moon': 'Księżyc',
    'milkyWay.maxAltitude': 'Maks. Wysokość',
    'milkyWay.moonIs': 'Księżyc jest',
    'milkyWay.considerWaiting': 'Rozważ poczekanie do',
    'milkyWay.newMoonParen': '(nów).',
    'milkyWay.greatNight': 'Świetna Noc!',
    'milkyWay.excellentConditions': 'Doskonałe warunki do fotografowania Drogi Mlecznej. Nie przegap!',
    'milkyWay.noIdealNights': 'Brak idealnych nocy w tym miesiącu. Sprawdź kolejny.',

    // Eclipse Planner
    'eclipse.solarEclipses': 'Zaćmienia Słońca',
    'eclipse.lunarEclipses': 'Zaćmienia Księżyca',
    'eclipse.showPast': 'Pokaż Przeszłe',
    'eclipse.hidePast': 'Ukryj Przeszłe',
    'eclipse.nextSolarEclipse': 'Najbliższe Zaćmienie Słońca',
    'eclipse.nextLunarEclipse': 'Najbliższe Zaćmienie Księżyca',
    'eclipse.maxDuration': 'Maks. czas trwania:',
    'eclipse.days': 'dni',
    'eclipse.viewDetailsOnTimeAndDate': 'Zobacz szczegóły na TimeAndDate.com',
    'eclipse.solar': 'Słoneczne',
    'eclipse.lunar': 'Księżycowe',
    'eclipse.eclipseCalendar': 'Kalendarz Zaćmień (2024-2030)',
    'eclipse.total': 'Całkowite',
    'eclipse.annular': 'Obrączkowe',
    'eclipse.partial': 'Częściowe',
    'eclipse.path': 'Trasa:',
    'eclipse.sarosCycle': 'Cykl Saros:',
    'eclipse.visibility': 'Widoczność:',
    'eclipse.magnitude': 'Wielkość:',
    'eclipse.totality': 'Faza całkowita:',
    'eclipse.viewMapAndTimes': 'Zobacz Mapę i Godziny',
    'eclipse.safetyWarningTitle': 'Ostrzeżenie Bezpieczeństwa:',
    'eclipse.safetyWarningText': 'Nigdy nie patrz bezpośrednio na słońce podczas zaćmienia bez odpowiednich filtrów solarnych z certyfikatem ISO. Używaj okularów do obserwacji zaćmień lub sprzętu do obserwacji słońca.',
    'eclipse.photographyTips': 'Porady do Fotografowania Zaćmień',
    'eclipse.tipFilterRequiredLabel': 'Wymagany filtr słoneczny',
    'eclipse.tipFilterRequiredText': 'Zdejmij tylko podczas fazy całkowitej',
    'eclipse.focalLength': 'Ogniskowa:',
    'eclipse.tipFocalLengthSolarText': '400-800mm dla detali tarczy słonecznej',
    'eclipse.tipBracketingLabel': 'Bracketing:',
    'eclipse.tipBracketingText': 'Rób wiele ekspozycji do kompozycji HDR',
    'eclipse.tipTotalityCaptureText': 'Uchwyć koronę, protuberancje, efekt diamentowego pierścienia',
    'eclipse.tipNoFilterLabel': 'Filtr niepotrzebny',
    'eclipse.tipNoFilterText': 'Księżyc można fotografować bezpośrednio',
    'eclipse.tipFocalLengthLunarText': '200-400mm dla detali Księżyca',
    'eclipse.tipSettingsLabel': 'Ustawienia:',
    'eclipse.tipSettingsText': 'Zacznij od ISO 400, f/8, 1/125s, dostosuj w trakcie faz',
    'eclipse.tipTotalityIncreaseText': 'Zwiększ ISO do 1600-6400 dla przyciemnionego czerwonego Księżyca',

    // Meteor Showers
    'meteor.nextShower': 'Najbliższy Rój Meteorów',
    'meteor.peak': 'Szczyt:',
    'meteor.days': 'dni',
    'meteor.activeNow': 'AKTYWNY TERAZ',
    'meteor.zhrLabel': 'ZHR (meteory/h)',
    'meteor.showerCalendar': 'Kalendarz Rojów Meteorów',
    'meteor.active': 'Aktywny',
    'meteor.veryGood': 'Bardzo Dobra',
    'meteor.activePeriod': 'Okres Aktywności',
    'meteor.moonInterference': 'Interferencja Księżyca',
    'meteor.illumination': 'oświetlenia',
    'meteor.speed': 'Prędkość',
    'meteor.radiantAlt': 'Wys. Radiantu',
    'meteor.max': 'maks.',
    'meteor.parent': 'Obiekt macierzysty:',
    'meteor.radiant': 'Radiant:',
    'meteor.peakIn': 'Szczyt za',
    'meteor.startPlanningShoot': 'zacznij planować sesję!',
    'meteor.photographyTips': 'Porady do Fotografowania Meteorów',
    'meteor.bestTime': 'Najlepszy czas:',
    'meteor.tipBestTimeText': 'Po północy, gdy radiant jest najwyżej',
    'meteor.settings': 'Ustawienia:',
    'meteor.tipSettingsText': 'Szeroki kąt, f/2.8 lub jaśniejszy, ISO 3200-6400, ekspozycje 15-25s',
    'meteor.tip': 'Wskazówka:',
    'meteor.tipFramingText': 'Kadruj z dala od radiantu, by uzyskać dłuższe ślady meteorów',
    'meteor.stack': 'Stackowanie:',
    'meteor.tipStackText': 'Zrób 100+ zdjęć i złóż najlepsze meteory w kompozycję',

    // Aurora Planner
    'aurora.title': 'Planer Zorzy Polarnej',
    'aurora.liveForecast': 'Prognoza na Żywo',
    'aurora.currentKpIndex': 'Aktualny Indeks Kp',
    'aurora.extremeStorm': 'Burza Ekstremalna',
    'aurora.severeStorm': 'Burza Silna',
    'aurora.strongStorm': 'Burza Mocna',
    'aurora.moderateStorm': 'Burza Umiarkowana',
    'aurora.minorStorm': 'Burza Słaba',
    'aurora.active': 'Aktywne',
    'aurora.unsettled': 'Niespokojne',
    'aurora.quiet': 'Spokojne',
    'aurora.yourVisibility': 'Twoja Widoczność',
    'aurora.possibleAtLatitude': 'Zorza możliwa na Twojej szerokości geograficznej!',
    'aurora.needKp': 'Potrzebny Kp',
    'aurora.forVisibilityAt': 'dla widoczności na szerokości',
    'aurora.kpIndexScale': 'Skala Indeksu Kp',
    'aurora.storm': 'Burza',
    'aurora.yourLocation': 'Twoja Lokalizacja:',
    'aurora.inAuroraZone': '🎯 Jesteś w strefie zorzy! Świetna lokalizacja do obserwacji zorzy polarnej.',
    'aurora.visibleStrongStorms': '⚡ Zorza widoczna podczas silnych burz geomagnetycznych (Kp 5+)',
    'aurora.visibleExtremeStorms': '🌟 Zorza widoczna tylko podczas ekstremalnych burz (Kp 7+)',
    'aurora.tooFarSouth': '📍 Jesteś zbyt daleko na południu na zorzę. Wyjedź na północ, by zwiększyć szanse.',
    'aurora.bestViewingTimes': 'Najlepsze Godziny Obserwacji (Następne 24h)',
    'aurora.peakForecast': 'Prognoza Szczytowa (3 dni)',
    'aurora.visible': 'Widoczna!',
    'aurora.kpForecast72h': 'Prognoza Kp na 72h',
    'aurora.night': 'Noc',
    'aurora.day': 'Dzień',
    'aurora.photographyTips': 'Porady do Fotografowania Zorzy',
    'aurora.settings': 'Ustawienia:',
    'aurora.tipSettingsText': 'ISO 1600-6400, f/2.8 lub jaśniejszy, ekspozycja 10-25 sek',
    'aurora.bestTime': 'Najlepszy czas:',
    'aurora.tipBestTimeText': '22:00 - 2:00 czasu lokalnego, podczas nowiu',
    'aurora.direction': 'Kierunek:',
    'aurora.tipDirectionText': 'Patrz na północ (lub południe na półkuli południowej)',
    'aurora.location': 'Lokalizacja:',
    'aurora.tipLocationText': 'Znajdź ciemne niebo z dala od zanieczyszczenia świetlnego',

    // Star Trails Simulator
    'startrails.title': 'Symulator Śladów Gwiazd',
    'startrails.northernHemisphere': 'Półkula Północna',
    'startrails.southernHemisphere': 'Półkula Południowa',
    'startrails.exposure': 'Ekspozycja:',
    'startrails.cameraDirection': 'Kierunek Kamery:',
    'startrails.focalLength': 'Ogniskowa:',
    'startrails.trailLength': 'Długość Śladu',
    'startrails.quick': 'Szybko:',
    'startrails.preset15': 'Ślad 15°',
    'startrails.preset45': 'Ślad 45°',
    'startrails.preset90': 'Ślad 90°',
    'startrails.presetFullCircle': 'Pełny okrąg',
    'startrails.animating': 'Animowanie...',
    'startrails.animateTrails': 'Animuj Ślady',
    'startrails.north': 'Północ',
    'startrails.trailCalculations': 'Obliczenia Śladów',
    'startrails.trailArc': 'Łuk Śladu',
    'startrails.totalTime': 'Całkowity Czas',
    'startrails.formulaLabel': 'Wzór:',
    'startrails.fullCircleLabel': 'Pełny okrąg (360°):',
    'startrails.hours': 'godzin',
    'startrails.tips': 'Porady do Śladów Gwiazd',
    'startrails.circumpolar': 'Okołobiegunowe:',
    'startrails.pointAt': 'Skieruj na',
    'startrails.polarisNorth': 'Gwiazda Polarna (Północ)',
    'startrails.southCelestialPole': 'Południowy Biegun Niebieski',
    'startrails.forCircularTrails': 'dla kolistych śladów',
    'startrails.equatorial': 'Równikowe:',
    'startrails.tipEquatorialText': 'Skieruj na Wschód/Zachód dla prostych, ukośnych śladów',
    'startrails.stacking': 'Stackowanie:',
    'startrails.tipStackingText': 'Zrób wiele 30-sekundowych ekspozycji i złóż je w postprodukcji',
    'startrails.gapFree': 'Bez przerw:',
    'startrails.tipGapFreeText': 'Użyj 1-2s odstępu między zdjęciami lub trybu bulb',
    'startrails.yourLatitude': 'Twoja szerokość geograficzna:',
    'startrails.circumpolarStarsWithin': '— gwiazdy okołobiegunowe w promieniu',
    'startrails.ofPole': 'od bieguna',
    'startrails.polaris': 'Gwiazda Polarna',
    'startrails.southPole': 'Biegun Południowy',
    'startrails.canvasTrail': 'Ślad:',

    // Calculators
    'calc.title': 'Kalkulatory Foto',
    'calc.exposure': 'Ekspozycja',
    'calc.dof': 'Głębia',
    'calc.starTrails': 'Ślady',
    'calc.iso': 'ISO',
    'calc.aperture': 'Przysłona',
    'calc.shutterSpeed': 'Czas',
    'calc.focalLength': 'Ogniskowa',
    'calc.distance': 'Odległość',
    'calc.apertureF': 'Przysłona (f/)',
    'calc.lightCondition': 'Warunki oświetleniowe (EV)',
    'calc.ev': 'EV',
    'calc.evBrightSunOnSnow': 'Jasne słońce na śniegu',
    'calc.evSunny': 'Słonecznie (zasada Sunny 16)',
    'calc.evSlightOvercast': 'Lekkie zachmurzenie',
    'calc.evOvercast': 'Pochmurno',
    'calc.evHeavyOvercast': 'Duże zachmurzenie',
    'calc.evOpenShade': 'Otwarty cień',
    'calc.evBrightIndoor': 'Jasne wnętrze',
    'calc.evIndoor': 'Wnętrze',
    'calc.evDimIndoor': 'Ciemne wnętrze',
    'calc.evCandlelight': 'Światło świec',
    'calc.evNightStreet': 'Ulica nocą',
    'calc.evFullMoon': 'Pełnia księżyca',
    'calc.evQuarterMoon': 'Kwadra księżyca',
    'calc.evStarlight': 'Światło gwiazd',
    'calc.evMilkyWay': 'Droga Mleczna',
    'calc.focalLengthMm': 'Ogniskowa (mm)',
    'calc.distanceM': 'Odległość (m)',
    'calc.sensorMm': 'Matryca (mm)',
    'calc.nearFocus': 'Bliska granica ostrości',
    'calc.farFocus': 'Daleka granica ostrości',
    'calc.totalDof': 'Całkowita głębia ostrości',
    'calc.hyperfocalDistance': 'Odległość hiperfokalna',
    'calc.focusHereFrom': 'Ustaw ostrość tutaj dla maksymalnej ostrości od',
    'calc.toInfinity': 'do ∞',
    'calc.cropFactor': 'Współczynnik przycięcia',
    'calc.pointStars': 'Punktowe gwiazdy (bez smug)',
    'calc.rule500': 'Zasada 500',
    'calc.starTrailsDuration': 'Czas trwania smug gwiazd',
    'calc.short': 'Krótkie',
    'calc.medium': 'Średnie',
    'calc.long': 'Długie',
    'calc.forOneHourStackedTrails': 'Dla godzinnych smug (stacking)',

    // DOF Calculator
    'dof.title': 'Kalkulator głębi ostrości',
    'dof.dofLabel': 'DOF',
    'dof.sensorSize': 'Rozmiar matrycy',
    'dof.sensorFullFrame': 'Pełna klatka',
    'dof.sensorApsCCanon': 'APS-C Canon',
    'dof.sensorApsC': 'APS-C Nikon/Sony',
    'dof.sensorMicro43': 'Micro 4/3',
    'dof.sensorMediumFormat': 'Średni format',
    'dof.sensor1Inch': '1 cal',
    'dof.focal': 'Ogniskowa',
    'dof.focus': 'Ostrość',
    'dof.focusAtHyperfocal': 'Ostrość na hiperfokalnej',
    'dof.results': 'Wyniki DOF',
    'dof.nearLimit': 'Granica bliska',
    'dof.farLimit': 'Granica daleka',
    'dof.totalDof': 'Całkowita głębia ostrości',
    'dof.hyperfocalDistance': 'Odległość hiperfokalna',
    'dof.focusHereMaxSharpness': 'Ustaw tu ostrość dla maks. ostrości do ∞',
    'dof.dofDistribution': 'Rozkład głębi ostrości',
    'dof.front': 'przed',
    'dof.back': 'za',
    'dof.oneThirdRule': 'Zasada ~1/3 przed, ~2/3 za',
    'dof.tipPortraitsLabel': 'Portrety:',
    'dof.tipPortraitsDesc': 'Szeroka przysłona (f/1.8-2.8) = płytka głębia ostrości, rozmyte tło',
    'dof.tipLandscapesLabel': 'Krajobrazy:',
    'dof.tipLandscapesDesc': 'Przymknięta przysłona (f/8-16) = duża głębia ostrości, wszystko ostre',
    'dof.tipHyperfocalLabel': 'Hiperfokalna:',
    'dof.tipHyperfocalDesc': 'Ustaw tu ostrość dla ostrości od połowy hiperfokalnej do nieskończoności',
    'dof.tipRuleLabel': 'Zasada 1/3:',
    'dof.tipRuleDesc': 'Głębia ostrości rozciąga się ~1/3 przed i ~2/3 za punktem ostrości',
    'dof.near': 'Blisko',
    'dof.far': 'Daleko',
    'dof.hf': 'HF',

    // FOV Calculator
    'fov.title': 'Kalkulator kąta widzenia',
    'fov.lensUltraWide': 'Bardzo szeroki',
    'fov.lensWide': 'Szeroki',
    'fov.lensNormal': 'Normalny',
    'fov.lensPortrait': 'Portretowy',
    'fov.lensTelephoto': 'Teleobiektyw',
    'fov.lensSuperTele': 'Super teleobiektyw',
    'fov.sensorSize': 'Rozmiar matrycy',
    'fov.sensorFullFrame': 'Pełna klatka (35mm)',
    'fov.sensorApsCCanon': 'APS-C (Canon)',
    'fov.sensorApsC': 'APS-C (Nikon/Sony)',
    'fov.sensorMicro43': 'Micro 4/3',
    'fov.sensorMediumFormat': 'Średni format',
    'fov.sensor1Inch': '1 cal',
    'fov.focalLength': 'Ogniskowa',
    'fov.equiv': 'ekw.',
    'fov.distance': 'Odległość',
    'fov.cameraDirection': 'Kierunek kamery',
    'fov.fovShownOnMap': 'Kąt widzenia widoczny na mapie',
    'fov.showFovOnMap': 'Pokaż kąt widzenia na mapie',
    'fov.details': 'Szczegóły kąta widzenia',
    'fov.horizontal': 'Poziomy',
    'fov.vertical': 'Pionowy',
    'fov.diagonal': 'Przekątny',
    'fov.coverage': 'Pokrycie',
    'fov.hyperfocalF8': 'Hiperfokalna (f/8)',
    'fov.useLandscapeLabel': 'Krajobraz:',
    'fov.useLandscapeDesc': '14-35mm (szeroki kąt: 63-114°)',
    'fov.usePortraitLabel': 'Portret:',
    'fov.usePortraitDesc': '85-135mm (wąski kąt: 15-24°)',
    'fov.useWildlifeLabel': 'Dzika przyroda:',
    'fov.useWildlifeDesc': '400-600mm (bardzo wąski: 3-5°)',
    'fov.useAstroLabel': 'Astro:',
    'fov.useAstroDesc': '14-24mm (szeroki kąt dla Drogi Mlecznej)',

    // Shadow Calculator
    'shadow.presetPerson': 'Osoba (1,75 m)',
    'shadow.presetTree': 'Drzewo (8 m)',
    'shadow.presetBuilding': 'Budynek (15 m)',
    'shadow.presetTower': 'Wieża (50 m)',
    'shadow.presetCustom': 'Własny',
    'shadow.title': 'Kalkulator cienia',
    'shadow.shadowLabel': 'Cień',
    'shadow.noShadow': 'Brak cienia',
    'shadow.noShadowSunBelowHorizon': 'Brak cienia (słońce pod horyzontem)',
    'shadow.time': 'Czas',
    'shadow.sunAlt': 'Wys. słońca',
    'shadow.sunAz': 'Azymut słońca',
    'shadow.objectHeight': 'Wysokość obiektu',
    'shadow.pause': 'Pauza',
    'shadow.animateDay': 'Animuj dzień',
    'shadow.shadowLength': 'Długość cienia',
    'shadow.timesObjectHeight': '× wysokości obiektu',
    'shadow.sunBelowHorizon': 'Słońce pod horyzontem',
    'shadow.shadowDirection': 'Kierunek cienia',
    'shadow.oppositeToSun': 'Przeciwnie do słońca',
    'shadow.formulaLabel': 'Wzór:',
    'shadow.formulaText': 'Długość cienia = Wysokość obiektu / tan(wysokość słońca)',
    'shadow.currentSunLabel': 'Aktualne słońce:',
    'shadow.alt': 'Wys.',
    'shadow.az': 'Az.',

    // Ephemeris
    'ephemeris.title': 'Tabele Efemerydy',
    'ephemeris.today': 'Dziś',
    'ephemeris.week': '7 Dni',
    'ephemeris.moonCal': 'Kalendarz',
    'ephemeris.twilight': 'Zmierzchy',
    'ephemeris.astronomical': 'Astronomiczny',
    'ephemeris.nautical': 'Żeglarski',
    'ephemeris.civil': 'Cywilny',
    'ephemeris.daylight': 'Dzień',
    'ephemeris.moonToday': 'Księżyc Dziś',
    'ephemeris.date': 'Data',
    'ephemeris.length': 'Długość',
    'ephemeris.moon': 'Księżyc',
    'ephemeris.newMoons': 'Nowie',
    'ephemeris.fullMoons': 'Pełnie',
    'ephemeris.noneThisMonth': 'Brak w tym miesiącu',
    'ephemeris.weekDaysShort': 'N,P,W,Ś,C,P,S',

    // Timeline 2D
    'timeline.title': 'Oś czasu 24h',
    'timeline.horizon': 'Horyzont',
    'timeline.selectedTime': 'Wybrana godzina',
    'timeline.sunAltitude': 'Wysokość Słońca',
    'timeline.conditions': 'Warunki',
    'timeline.night': 'Noc',

    // Find Sun/Moon
    'findsunmoon.title': 'Znajdź pozycję Słońca/Księżyca',
    'findsunmoon.sun': 'Słońce',
    'findsunmoon.moon': 'Księżyc',
    'findsunmoon.targetAzimuth': 'Docelowy azymut',
    'findsunmoon.targetElevation': 'Docelowa elewacja',
    'findsunmoon.horizon': 'Horyzont',
    'findsunmoon.low': 'Nisko',
    'findsunmoon.mid': 'Średnio',
    'findsunmoon.high': 'Wysoko',
    'findsunmoon.advancedFilters': 'Filtry zaawansowane',
    'findsunmoon.azTolerance': 'Tolerancja azymutu',
    'findsunmoon.elTolerance': 'Tolerancja elewacji',
    'findsunmoon.daysToSearch': 'Liczba dni wyszukiwania',
    'findsunmoon.searching': 'Wyszukiwanie...',
    'findsunmoon.findDates': 'Znajdź terminy',
    'findsunmoon.found': 'Znaleziono',
    'findsunmoon.results': 'wyników',
    'findsunmoon.sortDate': 'Data',
    'findsunmoon.sortElevation': 'Elewacja',
    'findsunmoon.sortPhase': 'Faza',
    'findsunmoon.day': 'Dzień',
    'findsunmoon.astroTwilight': 'Zmierzch astronomiczny',
    'findsunmoon.nauticalTwilight': 'Zmierzch żeglarski',
    'findsunmoon.az': 'Az',
    'findsunmoon.el': 'El',
    'findsunmoon.useCaseLabel': 'Zastosowanie:',
    'findsunmoon.useCaseText': 'Znajdź moment, gdy słońce zachodzi za konkretnym punktem orientacyjnym',
    'findsunmoon.azimuthLabel': 'Azymut:',
    'findsunmoon.azimuthText': '0°=N, 90°=E, 180°=S, 270°=W',
    'findsunmoon.clickResultLabel': 'Kliknij wynik',
    'findsunmoon.clickResultText': 'aby wczytać tę datę w planerze',

    // Map Layers
    'maplayers.layersButton': 'Warstwy',
    'maplayers.mapLayers': 'Warstwy mapy',
    'maplayers.goldenHourZone': 'Strefa złotej godziny',
    'maplayers.blueHourZone': 'Strefa błękitnej godziny',
    'maplayers.shadowDirection': 'Kierunek cienia',
    'maplayers.fovCone': 'Stożek pola widzenia',
    'maplayers.twilightZones': 'Strefy zmierzchu',
    'maplayers.fovSettings': 'Ustawienia pola widzenia',
    'maplayers.focalLength': 'Ogniskowa',
    'maplayers.direction': 'Kierunek',
    'maplayers.distance': 'Odległość',
    'maplayers.allOn': 'Wszystko wł.',
    'maplayers.allOff': 'Wszystko wył.',

    // Spots
    'spots.title': 'Moje Miejsca',
    'spots.addSpot': 'Dodaj Miejsce',
    'spots.noSpots': 'Brak zapisanych miejsc',
    'spots.useMyLocation': 'Użyj Mojej Lokalizacji',
    'spots.saveCurrentLocation': 'Zapisz tę lokalizację do Moich Miejsc',
    'spots.saveLocationPrompt': 'Nazwij to miejsce',
    'spots.saveLocationSuccess': 'Miejsce zapisane!',
    'spots.saveLocationError': 'Nie udało się zapisać miejsca. Zaloguj się najpierw.',
    'spots.nameRequired': 'Podaj nazwę miejsca',
    'spots.createdSuccess': 'Miejsce utworzone pomyślnie!',
    'spots.createError': 'Nie udało się utworzyć miejsca',
    'spots.deletedSuccess': 'Miejsce usunięte',
    'spots.deleteError': 'Nie udało się usunąć miejsca',
    'spots.locationUpdated': 'Lokalizacja zaktualizowana',
    'spots.locationError': 'Nie udało się pobrać lokalizacji',
    'spots.createSpot': 'Dodaj Nowe Miejsce',
    'spots.spotName': 'Nazwa',
    'spots.namePlaceholder': 'np. Widok ze Szczytu Góry',
    'spots.description': 'Opis',
    'spots.descriptionPlaceholder': 'Notatki o tym miejscu...',
    'spots.location': 'Lokalizacja',
    'spots.clickMapHint': 'Kliknij na mapie, aby ustawić lokalizację',
    'spots.latitude': 'Szerokość geograficzna',
    'spots.longitude': 'Długość geograficzna',
    'spots.saveSpot': 'Zapisz Miejsce',
    'spots.noSpotsHint': 'Kliknij "Dodaj Miejsce", aby zapisać pierwszą lokalizację',

    // Discover
    'discover.title': 'Odkryj Miejsca',
    'discover.search': 'Szukaj miejsc...',
    'discover.all': 'Wszystkie',
    'discover.mountains': 'Góry',
    'discover.urban': 'Miejskie',
    'discover.nature': 'Natura',
    'discover.astro': 'Astro',
    'discover.nearest': 'Najbliższe',
    'discover.rating': 'Ocena',
    'discover.score': 'Shot Score',
    'discover.minScore': 'Min. ocena',
    'discover.sort': 'Sortuj',
    'discover.spotsFound': 'znalezionych miejsc',
    'discover.by': 'od',
    'discover.away': 'stąd',
    'discover.reviews': 'opinii',
    'discover.noSpotsFound': 'Nie znaleziono miejsc',
    'discover.tryAdjustingFilters': 'Spróbuj zmienić filtry',
    'discover.shareYourSpots': 'Podziel się swoimi miejscami',
    'discover.helpCommunity': 'Pomóż społeczności odkrywać świetne miejsca',
    'discover.contribute': 'Dodaj miejsce',
    'discover.selected': 'Wybrano',
    'discover.spotSaved': 'Miejsce zapisane w Twojej kolekcji!',
    'discover.spotSaveFailed': 'Nie udało się zapisać miejsca. Zaloguj się najpierw.',

    // Alerts
    'alerts.title': 'Inteligentne Alerty',
    'alerts.enableNotifications': 'Włącz Powiadomienia',
    'alerts.addAlert': 'Dodaj Alert',
    'alerts.goldenHour': 'Złota Godzina',
    'alerts.blueHour': 'Błękitna Godzina',
    'alerts.shotScore': 'Próg Shot Score',
    'alerts.newMoon': 'Nów (Astro)',
    'alerts.startsIn': 'zaczyna się za',
    'alerts.minutesLabel': 'minut',
    'alerts.shotScoreIs': 'Ocena ujęcia wynosi',
    'alerts.greatConditions': 'Świetne warunki!',
    'alerts.newMoonTonight': 'Dziś nów',
    'alerts.perfectForAstro': 'Idealne warunki do astrofotografii!',
    'alerts.notificationsNotSupported': 'Ta przeglądarka nie obsługuje powiadomień',
    'alerts.notificationsEnabledMsg': 'Powiadomienia włączone!',
    'alerts.notificationPermissionDenied': 'Odmówiono zgody na powiadomienia',
    'alerts.alertCreated': 'Alert utworzony!',
    'alerts.alertDeleted': 'Alert usunięty',
    'alerts.noAlertsConfigured': 'Brak skonfigurowanych alertów',
    'alerts.noAlertsHint': 'Dodaj alerty, aby otrzymywać powiadomienia o idealnych warunkach',
    'alerts.whenScoreAbove': 'Gdy ocena ≥',
    'alerts.minBefore': 'min przed',
    'alerts.alertType': 'Typ alertu',
    'alerts.scoreThreshold': 'Próg oceny',
    'alerts.notifyBefore': 'Powiadom przed',

    // AI
    'ai.title': 'AI Predykcja Ujęcia',
    'ai.getRecommendation': 'Pobierz Rekomendację',
    'ai.analyzing': 'Analizuję...',
    'ai.todayRecommendation': 'Dzisiejsza Rekomendacja',
    'ai.landscape': 'Krajobraz',
    'ai.goldenHour': 'Złota Godzina',
    'ai.blueHour': 'Błękitna Godzina',
    'ai.astrophotography': 'Astrofotografia',
    'ai.urban': 'Architektura',

    // AR Camera View
    'ar.title': 'Widok AR z kamery',
    'ar.live': 'NA ŻYWO',
    'ar.cameraErrorDefault': 'Nie udało się uzyskać dostępu do kamery',
    'ar.httpsHint': 'Upewnij się, że dostęp do kamery jest udostępniony i korzystasz z HTTPS',
    'ar.pointCameraHint': 'Skieruj kamerę na niebo, aby zobaczyć nałożone pozycje słońca i księżyca',
    'ar.startCamera': 'Uruchom kamerę',
    'ar.timeOffset': 'Przesunięcie czasu',
    'ar.manualHeading': 'Kierunek ręczny',
    'ar.stopCamera': 'Zatrzymaj kamerę',
    'ar.sun': 'Słońce',
    'ar.moon': 'Księżyc',
    'ar.azimuth': 'Azymut',
    'ar.altitude': 'Wysokość',
    'ar.heading': 'Kierunek',
    'ar.time': 'Czas',
    'ar.tipMobileTitle': 'Najlepiej na telefonie:',
    'ar.tipMobileText': 'Użyj kompasu telefonu dla dokładnego kierunku',
    'ar.tipCalibrateTitle': 'Kalibracja:',
    'ar.tipCalibrateText': 'Poruszaj telefonem w kształcie ósemki, aby skalibrować kompas',
    'ar.tipTimeScrubTitle': 'Przesuwanie czasu:',
    'ar.tipTimeScrubText': 'Dostosuj przesunięcie czasu, aby zobaczyć przyszłe pozycje',
    'ar.tipUseCaseTitle': 'Zastosowanie:',
    'ar.tipUseCaseText': 'Znajdź dokładne miejsce, w którym słońce zajdzie za punktem orientacyjnym',

    // View 3D Planner
    'view3d.seeRealPhotos': 'Zobacz prawdziwe zdjęcia z tej lokalizacji:',
    'view3d.realStreetView': 'Prawdziwy widok ulicy',
    'view3d.sunPositionSimulator': 'Symulator pozycji słońca',
    'view3d.daylight': 'Dzień',
    'view3d.twilight': 'Zmierzch',
    'view3d.night': 'Noc',
    'view3d.direction': 'Kierunek',
    'view3d.look': 'Spojrzenie',
    'view3d.focal': 'Ogniskowa',
    'view3d.time': 'Czas',
    'view3d.pause': 'Pauza',
    'view3d.animateDay': 'Animuj dzień',
    'view3d.faceSun': 'Skieruj na słońce',
    'view3d.goldenAM': 'Złota poranna',
    'view3d.noon': 'Południe',
    'view3d.goldenPM': 'Złota wieczorna',
    'view3d.sunAzimuth': 'Azymut słońca',
    'view3d.sunAltitude': 'Wysokość słońca',
    'view3d.cameraFov': 'Kąt widzenia obiektywu',

    // Equipment
    'equipment.title': 'Mój Sprzęt',
    'equipment.addEquipment': 'Dodaj Sprzęt',
    'equipment.camera': 'Aparat',
    'equipment.lens': 'Obiektyw',
    'equipment.tripod': 'Statyw',
    'equipment.filter': 'Filtr',
    'equipment.drone': 'Dron',
    'equipment.accessory': 'Akcesoria',
    'equipment.other': 'Inne',
    'equipment.filterByCategory': 'Filtruj wg kategorii',
    'equipment.allCategories': 'Wszystkie Kategorie',
    'equipment.name': 'Nazwa',
    'equipment.namePlaceholder': 'np. Canon EOS R5',
    'equipment.category': 'Kategoria',
    'equipment.notes': 'Notatki',
    'equipment.notesPlaceholder': 'Numer seryjny, ustawienia, itp.',
    'equipment.noEquipmentTitle': 'Brak Sprzętu',
    'equipment.noEquipmentSubtitle': 'Zacznij tworzyć listę swojego sprzętu',
    'equipment.addFirstItem': 'Dodaj Pierwszy Element',
    'equipment.nameRequired': 'Podaj nazwę sprzętu',
    'equipment.addedSuccess': 'Sprzęt dodany pomyślnie!',
    'equipment.addError': 'Nie udało się dodać sprzętu',
    'equipment.deletedSuccess': 'Sprzęt usunięty',
    'equipment.deleteError': 'Nie udało się usunąć sprzętu',

    // Auth
    'auth.login': 'Zaloguj',
    'auth.register': 'Rejestracja',
    'auth.logout': 'Wyloguj',
    'auth.email': 'Email',
    'auth.password': 'Hasło',
    'auth.name': 'Imię',
    'auth.signIn': 'Zaloguj się',
    'auth.createAccount': 'Utwórz Konto',
    'auth.welcomeBack': 'Witaj ponownie',
    'auth.createYourAccount': 'Utwórz swoje konto',
    'auth.welcomeBackToast': 'Witaj ponownie!',
    'auth.loginFailed': 'Logowanie nie powiodło się',
    'auth.backToHome': 'Powrót do strony głównej',
    'auth.signInSubtitle': 'Zaloguj się, aby kontynuować planowanie idealnego światła',
    'auth.emailPlaceholder': 'twoj@email.pl',
    'auth.noAccount': 'Nie masz konta?',
    'auth.createOne': 'Utwórz je',
    'auth.passwordMinLength': 'Hasło musi mieć co najmniej 6 znaków',
    'auth.accountCreated': 'Konto utworzone pomyślnie!',
    'auth.registrationFailed': 'Rejestracja nie powiodła się',
    'auth.registerSubtitle': 'Zacznij planować swoje idealne zdjęcia już dziś',
    'auth.namePlaceholder': 'Twoje imię',
    'auth.passwordMinPlaceholder': 'Min. 6 znaków',
    'auth.haveAccount': 'Masz już konto?',

    // Landing
    'landing.hero': 'Planuj Idealne Światło.',
    'landing.subtitle': 'Przestań zgadywać. LuminaSpot analizuje tysiące zmiennych, by powiedzieć Ci dokładnie gdzie i kiedy zrobić najlepsze zdjęcie.',
    'landing.getStarted': 'Zacznij Planować',
    'landing.features': 'Wszystko Czego Potrzebujesz',
    'landing.featureSunTitle': 'Śledzenie Słońca i Księżyca',
    'landing.featureSunDesc': 'Precyzyjne obliczenia wschodu, zachodu, złotej i błękitnej godziny. Wiedz dokładnie, kiedy światło jest idealne.',
    'landing.featureScoreDesc': 'Nasz algorytm analizuje pogodę, widoczność i warunki astronomiczne, oceniając potencjał ujęcia w skali 0-100%.',
    'landing.featureScoutingTitle': 'Inteligentny Scouting',
    'landing.featureScoutingDesc': 'Zapisuj ulubione lokalizacje ze współrzędnymi GPS. Miej do nich dostęp wszędzie i planuj z wyprzedzeniem.',
    'landing.featureWeatherDesc': 'Wielowarstwowa analiza chmur, prognozy widoczności i warunki atmosferyczne na pierwszy rzut oka.',
    'landing.getStartedNav': 'Zacznij',
    'landing.badge': 'Planowanie Fotografii Nowej Generacji',
    'landing.heroLine1': 'Planuj Idealne',
    'landing.heroLine2': 'Światło.',
    'landing.haveAccount': 'Mam już konto',
    'landing.statWeather': 'Prognoza Pogody',
    'landing.statAccuracy': 'Trafność Oceny Ujęcia',
    'landing.statLocations': 'Zapisane Lokalizacje',
    'landing.featuresSubtitle': 'Profesjonalne narzędzia inspirowane PhotoPills, zaprojektowane od nowa dla współczesnego fotografa',
    'landing.ctaTitlePrefix': 'Gotowy, by Uchwycić',
    'landing.ctaTitleHighlight': 'Idealne Światło',
    'landing.ctaSubtitle': 'Dołącz do fotografów, którzy planują mądrzej, nie ciężej. Twoje kolejne arcydzieło zaczyna się od idealnego planu.',
    'landing.createFreeAccount': 'Załóż Darmowe Konto',
    'landing.footerTagline': '© 2025 LuminaSpot. Planuj Idealne Światło.',

    // Settings
    'settings.title': 'Ustawienia',
    'settings.theme': 'Motyw',
    'settings.light': 'Jasny',
    'settings.dark': 'Ciemny',
    'settings.auto': 'Auto',
    'settings.language': 'Język',

    // Common
    'common.save': 'Zapisz',
    'common.cancel': 'Anuluj',
    'common.delete': 'Usuń',
    'common.edit': 'Edytuj',
    'common.loading': 'Ładowanie...',
    'common.error': 'Błąd',
    'common.success': 'Sukces',
    'common.morning': 'Rano',
    'common.evening': 'Wieczór',
  }
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('luminaspot_language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('luminaspot_language', language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en'][key] || key;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  const availableLanguages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  ];

  return (
    <I18nContext.Provider value={{ language, t, changeLanguage, availableLanguages }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};
